import type { AuditDataSource } from '../AuditDataSource';
import type {
  AuditBatch,
  AuditCase,
  AuditSubmission,
  CodebookHit,
  CodeCaseList,
  CodeCaseQuery,
  CodeCaseRow,
  MyDashboard,
  OrgDashboard,
  AuditQueue,
} from '../types';
import {
  AUDIT_CASES_INTERNAL,
  buildBatches,
  toBlindCase,
} from '../data/cases';
import { AUDIT_CODEBOOK } from '../data/codebook';
import { AUDIT_DASHBOARD, AUDITOR_STATS } from '../data/dashboard';

// Static, backendless AuditDataSource for the preview + tests (HUB-504). Returns
// the same shapes a live @hub/api source will — so audit primitives don't know the
// difference — but is fully deterministic. It performs the blind conversion
// (`toBlindCase`) so provenance never leaves this module: the batches, case
// summaries, and single-case results it hands out expose only opaque A/B slots.
// No PHI (invented sample data).

const BATCHES: AuditBatch[] = buildBatches(AUDIT_CASES_INTERNAL);

/**
 * Mock queues: per-reviewer, so the demo shows the shape production is moving
 * to rather than the shared queue it is moving away from.
 *
 * Deterministic round-robin here, NOT the server's rendezvous hash — the mock
 * only has to produce a plausible spread, and reimplementing the routing would
 * create a second copy of it that could drift from the real one while both
 * looked fine.
 *
 * One batch is deliberately left in a shared queue with two members, because
 * that is the transitional state the UI has to render honestly: it must not
 * claim a single owner for work more than one person can see.
 */
const MOCK_REVIEWERS = ['a.chen', 'r.patel', 'm.okafor'];
const MOCK_QUEUES: AuditQueue[] = (() => {
  const perPerson = MOCK_REVIEWERS.map((r) => ({
    id: `rv-${r}`,
    name: r,
    batchIds: [] as string[],
    reviewerIds: [r],
  }));
  const shared = {
    id: 'reviewers',
    name: 'Unassigned pool',
    batchIds: [] as string[],
    reviewerIds: [MOCK_REVIEWERS[0], MOCK_REVIEWERS[1]],
  };
  BATCHES.forEach((b, i) => {
    if (i % 7 === 3) shared.batchIds.push(b.id);
    else perPerson[i % perPerson.length].batchIds.push(b.id);
  });
  return [...perPerson, shared];
})();

export const mockAuditSource: AuditDataSource = {
  async fetchBatches(): Promise<AuditBatch[]> {
    return BATCHES;
  },

  async fetchQueues(): Promise<AuditQueue[]> {
    return MOCK_QUEUES;
  },

  async fetchBatch(id: string): Promise<AuditBatch | null> {
    return BATCHES.find((b) => b.id === id) ?? null;
  },

  async fetchCase(id: string): Promise<AuditCase | null> {
    const internal = AUDIT_CASES_INTERNAL.find((c) => c.id === id);
    return internal ? toBlindCase(internal) : null;
  },

  async searchCodebook(query: string): Promise<CodebookHit[]> {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return Object.entries(AUDIT_CODEBOOK)
      .filter(
        ([code, entry]) =>
          code.toLowerCase().includes(q) ||
          entry.title.toLowerCase().includes(q) ||
          entry.desc.toLowerCase().includes(q),
      )
      .map(([code, entry]) => ({ code, kind: entry.kind, title: entry.title, desc: entry.desc }));
  },

  async submitAudit(_submission: AuditSubmission): Promise<void> {
    // No-op in the mock — the review-submission slice wires the live mutation.
    return;
  },

  async fetchOrgDashboard(): Promise<OrgDashboard> {
    return AUDIT_DASHBOARD;
  },

  async fetchCodeCases(q: CodeCaseQuery): Promise<CodeCaseList> {
    const code = q.code ?? "45378";
    const field = q.field ?? "surgical_cpt";
    const rows: CodeCaseRow[] = ["AC-100482", "AC-100517", "AC-100603"].map((id, i) => ({
      id, episodeId: `EP-${100 + i}`, batchId: "intake-2026-09-01",
      receivedAt: `2026-09-0${i + 1}T14:00:00.000Z`, receivedAtEpoch: Date.UTC(2026, 8, i + 1, 14),
      reviewSubmittedAt: q.mode === "errored" ? `2026-09-0${i + 4}T09:00:00.000Z` : "",
      field, code, side: q.mode === "errored" ? (i % 2 ? "coder" : "agent") : "",
      verdict: q.mode === "errored" ? (i % 2 ? "agent" : "coder") : "",
      reasons: q.mode === "errored" ? [q.reason ?? "proc_detail_off"] : [], matched: i === 0,
    }));
    return { rows, total: rows.length };
  },

  async fetchMyDashboard(): Promise<MyDashboard> {
    return AUDITOR_STATS;
  },
};
